import { ReactNode, useId } from 'react';
import { alpha, Box, Divider, Paper, Stack, Typography } from '@mui/material';

interface IProps {
  title: string;
  /** One line on what the section asks, under its title. */
  description?: string;
  icon: ReactNode;
  children: ReactNode;
}

/**
 * One titled card of an acquisition form. Every product groups its questions
 * the same way, so the operator reads the three forms as one workspace.
 */
const AcquisitionFormSection = ({
  title,
  description,
  icon,
  children,
}: IProps) => {
  const titleId = useId();

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby={titleId}
      sx={{ mb: 3 }}
    >
      <Stack direction="row" spacing={2} alignItems="center" px={3} py={2}>
        <Box
          aria-hidden
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: 40,
            height: 40,
            color: 'secondary.main',
            bgcolor: alpha(theme.palette.secondary.main, 0.08),
          })}
        >
          {icon}
        </Box>
        <Box>
          <Typography
            id={titleId}
            variant="subtitle1"
            component="h2"
            fontWeight={700}
            color="primary.main"
          >
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
      <Divider />
      <Box p={3}>{children}</Box>
    </Paper>
  );
};

export default AcquisitionFormSection;
